'use client';

import { usePathname } from 'next/navigation';
import type { MouseEvent } from 'react';
import { AUTH_ROUTES, isLegalRoute, ONBOARDING_ROUTES } from '@/app/routes';
import { usePublicRoute } from '@/hooks/usePublicRoute/usePublicRoute';
import { cn } from '@/libs/utils/utils';
import {
  HeaderContainer,
  HeaderExploreNavigationButtons,
  HeaderOnboarding,
  HeaderTitle,
} from '@/molecules/Header/Header';
import { HeaderHome } from '@/molecules/HeaderHome/HeaderHome';
import { HeaderSignIn } from '@/molecules/HeaderSignIn/HeaderSignIn';
import { Logo } from '@/molecules/Logo/Logo';
import { selectDisplayUserPubky } from '@/stores/auth/auth.selectors';
import { useAuthStore } from '@/stores/auth/auth.store';
import { pathToStepConfig } from './Header.constants';

export function Header() {
  const pathname = usePathname();
  const isAuthenticated = useAuthStore((state) => Boolean(selectDisplayUserPubky(state)));
  const { isCoreExploreRoute, isDynamicPublicRoute } = usePublicRoute();

  const isOnboarding = pathname?.startsWith('/onboarding') ?? false;
  const isLandingPage = pathname === '/';
  const isCopyrightPage = pathname === '/copyright';
  // The migration gate holds the account out of the store: no account chrome, no own-profile query.
  const isMigrationPage = pathname === AUTH_ROUTES.MIGRATION;
  const isLegalPage = isLegalRoute(pathname);
  const stepConfig = pathname ? pathToStepConfig[pathname] : undefined;
  const currentStep = stepConfig?.step ?? 1;
  const currentTitle = stepConfig?.title;
  // Onboarding steps reached after authentication (profile setup and the Experience screens): the
  // logo must not link away mid-flow. Step numbers are not unique in the 4-step model, so match on
  // the path instead.
  const isPostAuthOnboardingStep =
    pathname === ONBOARDING_ROUTES.PROFILE ||
    pathname === ONBOARDING_ROUTES.TAGS ||
    pathname === ONBOARDING_ROUTES.FOLLOW;

  // Hide header on mobile when:
  // - User is on a core explore route (/home, /hot, /search, /collections) — MobileHeader + MobileFooter
  // - Any user on a dynamic public route (/post/..., /profile/[pubky], /collections/[userId]/[postId]) — page shell owns mobile chrome
  // - Authenticated on standard app routes — MobileHeader + MobileFooter
  // Legal pages have no mobile app shell, so their header (and its way back) stays on every viewport.
  const shouldHideHeaderOnMobile =
    !isLegalPage &&
    (isCoreExploreRoute || isDynamicPublicRoute || (isAuthenticated && !isOnboarding && !isDynamicPublicRoute));
  // Show the step title on every onboarding page regardless of auth state: sign-up happens on the
  // pubky step, so the later key steps (pubky, backup) and the post-auth steps (profile, experience)
  // are all reached while authenticated. Outside onboarding (i.e. /logout) the title is only for
  // signed-out users.
  const shouldShowTitle = currentTitle && (isOnboarding || !isAuthenticated);

  // Determine which header content to show:
  // - Onboarding: HeaderOnboarding
  // - Authenticated: HeaderSignIn (navigation + avatar)
  // - Unauthenticated on core explore or dynamic public routes (home/hot/search/collections/post/profile): explore navigation + join
  // - Unauthenticated on landing/other: HeaderHome (social links + sign in)
  const renderHeaderContent = () => {
    if (isOnboarding) {
      return <HeaderOnboarding currentStep={currentStep} />;
    }
    if (isAuthenticated) {
      return <HeaderSignIn />;
    }
    if (isCoreExploreRoute || isDynamicPublicRoute) {
      return <HeaderExploreNavigationButtons />;
    }
    return <HeaderHome />;
  };

  // Copyright and the migration gate show only the logo (minimal header).
  if (isCopyrightPage || isMigrationPage) {
    return (
      <HeaderContainer>
        <Logo />
      </HeaderContainer>
    );
  }

  const handleLandingLogoClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isLandingPage) return;

    event.preventDefault();
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  return (
    <HeaderContainer className={cn(shouldHideHeaderOnMobile && 'hidden lg:block')}>
      <Logo noLink={isPostAuthOnboardingStep} onClick={handleLandingLogoClick} />
      {shouldShowTitle && <HeaderTitle currentTitle={currentTitle} />}
      {renderHeaderContent()}
    </HeaderContainer>
  );
}
